import { useState } from "react";
import { authService } from "../../services/api";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { ScopePicker, CurrencyChoice, CurrencyToggles, OptionChoice } from "./shared";

/**
 * Conexão por credencial de marcas de cartão (Eagle e as próximas).
 * O formulário vem de `gateway.credential_fields`, declarado pelo backend
 * (provider da marca): plugar uma marca nova não exige tela nova.
 * Segredo salvo nunca volta para o browser — o campo fica vazio e em branco
 * mantém o valor atual.
 */
export function isFormConnected(item) {
    const fields = item?.gateway?.credential_fields;
    if (!fields?.length) return false;
    // Várias opções (ex.: moedas aceitas), liga/desliga e escolha com padrão
    // não decidem conexão.
    return fields.every((f) => f.multiple || f.toggle || f.default || Boolean(item.credentials?.[f.key]));
}

const initialValue = (f, creds) => {
    if (f.toggle) return creds?.[f.key] === true;
    if (f.multiple) {
        if (Array.isArray(creds?.[f.key])) return creds[f.key];
        // Nada salvo: o que o backend já cobra nesse caso (vazio = todas).
        return f.default_options?.length ? [...f.default_options] : [...f.options];
    }
    return f.secret ? "" : (creds?.[f.key] || f.default || "");
};

const initialValues = (fields, creds) =>
    Object.fromEntries(fields.map((f) => [f.key, initialValue(f, creds)]));

export default function CardCredentialsPanel({ item, bots, onDone }) {
    const { showError, showSuccess } = useNotification();
    const fields = item.gateway.credential_fields || [];
    const creds = item.credentials || {};
    const name = item.gateway.name;
    const connected = isFormConnected(item);
    const hasScope = Boolean(item.upg?.id);

    const [values, setValues] = useState(() => initialValues(fields, creds));
    const [scope, setScope] = useState(item.upg?.scope === "specific" ? "specific" : "global");
    const [botIds, setBotIds] = useState(item.upg?.scoped_bot_ids || []);
    const [busy, setBusy] = useState(false);

    const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));
    const toggleOption = (key, opt) => setValues((prev) => ({
        ...prev,
        [key]: prev[key].includes(opt) ? prev[key].filter((x) => x !== opt) : [...prev[key], opt],
    }));
    const toggleBot = (id) => setBotIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

    const save = async (e) => {
        e.preventDefault();
        const emptyMulti = fields.find((f) => f.multiple && !values[f.key].length);
        if (emptyMulti) {
            showError(`Selecione ao menos uma opção em ${emptyMulti.label}.`);
            return;
        }
        if (hasScope && scope === "specific" && botIds.length === 0) {
            showError("Selecione ao menos um bot.");
            return;
        }
        try {
            setBusy(true);
            await authService.gateways.saveCardCredentials(name, values);
            if (hasScope) {
                await authService.gateways.saveScope(name, scope, scope === "specific" ? botIds : []);
            }
            showSuccess("Credenciais validadas e salvas.");
            onDone?.();
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao salvar credenciais.");
        } finally {
            setBusy(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Desconectar? As chaves secretas são apagadas e a marca para de cobrar.")) return;
        try {
            setBusy(true);
            await authService.gateways.disconnectCard(name);
            showSuccess("Desconectado.");
            onDone?.();
        } catch (err) {
            showError(err?.response?.data?.error || "Erro ao desconectar.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="spt-form" onSubmit={save}>
            {fields.map((f) => {
                // Campo condicional: só aparece quando o outro campo tem o valor pedido.
                if (f.show_if && values[f.show_if.key] !== f.show_if.value) return null;
                const id = `card-cred-${name}-${f.key}`;
                if (f.toggle) {
                    return (
                        <div key={f.key} className="flex w-full items-center justify-between gap-3 pt-1">
                            <label htmlFor={id} className="cursor-pointer text-[13px] text-white/80">{f.label}</label>
                            <label className="toggle-wrapper toggle-sm shrink-0">
                                <input id={id} type="checkbox" checked={values[f.key]}
                                    onChange={(e) => setValue(f.key, e.target.checked)} />
                                <span className="toggle-slider" />
                            </label>
                        </div>
                    );
                }
                if (f.multiple) {
                    return (
                        <div key={f.key} className="w-full space-y-2">
                            <span id={id} className="text-xs font-medium text-white/40">{f.label}</span>
                            <CurrencyToggles value={values[f.key]} options={f.options} labelledBy={id}
                                onToggle={(opt) => toggleOption(f.key, opt)} />
                        </div>
                    );
                }
                if (f.options?.length && f.option_labels?.length) {
                    return (
                        <div key={f.key} className="w-full space-y-2">
                            <span id={id} className="text-xs font-medium text-white/40">{f.label}</span>
                            <OptionChoice value={values[f.key]} options={f.options} labels={f.option_labels}
                                labelledBy={id} onChange={(opt) => setValue(f.key, opt)} />
                        </div>
                    );
                }
                if (f.options?.length) {
                    return (
                        <div key={f.key} className="w-full space-y-2">
                            <span id={id} className="text-xs font-medium text-white/40">{f.label}</span>
                            <CurrencyChoice value={values[f.key]} options={f.options} labelledBy={id}
                                onChange={(opt) => setValue(f.key, opt)} />
                        </div>
                    );
                }
                return (
                    <div key={f.key} className="w-full space-y-1">
                        <label htmlFor={id} className="text-xs font-medium text-white/40">{f.label}</label>
                        <input id={id} className="input2" autoComplete={f.secret ? "new-password" : "off"}
                            type={f.secret ? "password" : "text"} value={values[f.key]}
                            placeholder={f.secret && creds[f.key] ? `${f.label} (preencha p/ alterar)` : f.label}
                            onChange={(e) => setValue(f.key, e.target.value)} />
                    </div>
                );
            })}

            {hasScope && (
                <div className="mt-3 w-full">
                    <ScopePicker scope={scope} setScope={setScope} bots={bots} botIds={botIds} toggleBot={toggleBot} />
                </div>
            )}

            <div className="spt-form-actions">
                {connected && (
                    <button type="button" onClick={disconnect} disabled={busy}
                        className="btn-secondary border-red-500/30 text-red-400 hover:text-red-300">
                        Desconectar
                    </button>
                )}
                <button type="button" className="btn-secondary" onClick={() => onDone?.()}>Cancelar</button>
                <button className="btn-primary" type="submit" disabled={busy}>
                    {busy ? "Salvando..." : "Salvar"}
                </button>
            </div>
        </form>
    );
}
